import { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'wouter'
import { PencilEdit01Icon } from '@hugeicons/core-solid-rounded'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import { dateTime } from '@guestgoodie/helpers'

import { AddButton, IconButton } from '@/components/ui/IconButton'
import Button from '@/components/ui/Button'
import Dropdown from '@/components/ui/Dropdown'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { CheckPermission, Permission } from '@/features/auth'
import useLayouts from '../hooks/useLayouts'
import { useLayoutStateToLabel } from '../utils/helpers'

export function LayoutHeader({
  layout,
  canUpdateLayout
}: {
  layout?: GraphQL.LayoutFragmentFragment
  canUpdateLayout: boolean
}) {
  const { t } = useLingui()
  const [, navigate] = useLocation()
  const layoutStayToLabel = useLayoutStateToLabel()

  const {
    layouts,
    create,
    publish,
    remove,
    publishLoading,
    createLoading,
    removeLoading,
    changeLayout
  } = useLayouts()

  useEffect(() => {
    if (layout) return

    const version = layouts.find(
      l => l.state === GraphQL.LayoutState.Published
    )?.version

    navigate(`/layout/${version}`)
  }, [layout, layouts, navigate])

  const onCreate = useCallback(
    () => layout && create(layout.id),
    [create, layout]
  )
  const onPublish = useCallback(
    () => layout && publish(layout.id),
    [publish, layout]
  )

  const layoutOptions = useMemo(() => {
    return layouts.map(function (layout) {
      // TODO: Currently always shown in UTC
      const date = dateTime.fullFormatter.format(
        new Date(layout.publishedAt ?? layout.insertedAt)
      )

      const state = layoutStayToLabel[layout.state]
      const version = layout.version

      return {
        label: t({
          comment:
            'Label to show to the user to describe a specific version of a layout.',
          message: `${state}: Version ${version} (${date})`
        }),
        value: layout.version
      }
    })
  }, [layouts, layoutStayToLabel, t])

  const addCategory = useCallback(() => {
    if (!layout?.version) return

    navigate(`/create-category/${layout.version}`)
  }, [layout?.version, navigate])

  const layoutVersion = layout?.version

  return (
    <div className="flex flex-col md:flex-row md:justify-between gap-2.5">
      <div className="flex flex-row gap-2.5 items-center">
        {layout && (
          <>
            {canUpdateLayout && (
              <>
                {layout.state === GraphQL.LayoutState.Draft ? (
                  <AddButton onClick={addCategory} />
                ) : (
                  <IconButton
                    icon={PencilEdit01Icon}
                    onClick={onCreate}
                    loading={createLoading}
                    aria-label={t`Create Category`}
                  />
                )}
              </>
            )}
            <h2 className="text-heading2 text-text">
              <Trans>Categories</Trans>
            </h2>
          </>
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-2.5 md:items-center">
        <div>
          <Dropdown<number>
            placeholder={t`Choose a layout`}
            onChange={changeLayout}
            options={layoutOptions}
            value={layout?.version}
          />
        </div>
        <div className="flex flex-row gap-2">
          <CheckPermission permissions={[Permission.LAYOUT_DELETE]}>
            {layout && layout.state !== GraphQL.LayoutState.Published && (
              <div>
                <ConfirmDialog
                  asChild
                  title={t`Delete Layout`}
                  message={t`Are you sure that you want to delete the layout with Version "${layoutVersion}"?`}
                  onConfirm={() => remove(layout.id)}
                >
                  <Button variant="outline" loading={removeLoading}>
                    <Trans>Delete</Trans>
                  </Button>
                </ConfirmDialog>
              </div>
            )}
          </CheckPermission>
          {canUpdateLayout &&
            layout &&
            layout.state === GraphQL.LayoutState.Draft && (
              <div>
                <Button loading={publishLoading} onClick={onPublish}>
                  <Trans>Publish</Trans>
                </Button>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
